import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { HorizontalScrollTable } from '../components/ui/horizontal-scroll-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import ZohoSyncStamp from '../components/layout/ZohoSyncStamp';
import { toast } from 'sonner';
import { Upload, Download, Loader2, FileSpreadsheet, Zap, Pencil, Search, ArrowRight, History, AlertTriangle, Check } from 'lucide-react';
import { VendorCombobox } from '../components/VendorCombobox';

const STATUS_COLORS = {
  'BID GENERATED': 'bg-emerald-100 text-emerald-800',
  'NO PM ASSIGNED FOR THIS ITEM': 'bg-amber-100 text-amber-800',
  'NO PM TARGET FOUND FOR THIS ITEM': 'bg-blue-100 text-blue-800',
  'NO RELIABLE INVENTORY MATCH FOUND': 'bg-red-100 text-red-800',
  'MULTIPLE POSSIBLE MATCH - DO MANUAL MATCH': 'bg-purple-100 text-purple-800',
  'LOW CONFIDENCE - REVIEW REQUIRED': 'bg-orange-100 text-orange-800',
  'AI Processing Failed': 'bg-red-200 text-red-900',
};
const MATCH_COLORS = { 'EXACT': 'bg-emerald-100 text-emerald-800', 'ALIAS': 'bg-blue-100 text-blue-800', 'AI': 'bg-pink-100 text-pink-800', 'AI-MPN': 'bg-pink-100 text-pink-800', 'AI-CLAUDE': 'bg-purple-100 text-purple-800', 'ATTRIBUTE': 'bg-cyan-100 text-cyan-800', 'ATTRIBUTE-MODEL': 'bg-cyan-100 text-cyan-800', 'MANUAL': 'bg-violet-100 text-violet-800' };
const REC_COLORS = { 'BUY': 'bg-emerald-600 text-white', 'NEGOTIATE': 'bg-amber-500 text-white', 'REJECT': 'bg-red-600 text-white', 'REVIEW': 'bg-gray-200 text-gray-800' };
const VENDOR_CURRENCIES = ['USD', 'EUR', 'GBP', 'AED', 'CNY'];

// Round 36 (next round, item 4): Bid History time-frame filter — calendar dates are
// computed against "today" in Hong Kong (the backend converts them to HK 00:00-23:59:59
// boundaries in UTC), not the viewer's own browser timezone.
function hkTodayStr() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Hong_Kong' }).format(new Date());
}
function addDaysStr(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function addMonthsStr(dateStr, months) {
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}
const HISTORY_PERIODS = [
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last 7 Days' },
  { id: '15d', label: 'Last 15 Days' },
  { id: '30d', label: 'Last 30 Days' },
  { id: '3mo', label: 'Last 3 Months' },
  { id: 'all', label: 'All Time' },
  { id: 'custom', label: 'Custom Range' },
];
const HISTORY_PERIOD_RANGE = {
  yesterday: () => { const t = hkTodayStr(); const y = addDaysStr(t, -1); return { from: y, to: y }; },
  '7d': () => { const t = hkTodayStr(); return { from: addDaysStr(t, -6), to: t }; },
  '15d': () => { const t = hkTodayStr(); return { from: addDaysStr(t, -14), to: t }; },
  '30d': () => { const t = hkTodayStr(); return { from: addDaysStr(t, -29), to: t }; },
  '3mo': () => { const t = hkTodayStr(); return { from: addMonthsStr(t, -3), to: t }; },
  all: () => ({ from: '', to: '' }),
};

export default function BidEnginePage() {
  const { isAdmin, user } = useAuth();
  const isPreviewEnv = user?.app_env === 'preview';
  const [searchParams] = useSearchParams();
  const [file, setFile] = useState(null);
  const [vendorName, setVendorName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [results, setResults] = useState(null);
  const [stats, setStats] = useState(null);
  const [columnsDetected, setColumnsDetected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [changeRow, setChangeRow] = useState(null);
  const [invSearch, setInvSearch] = useState('');
  const [invResults, setInvResults] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [recentJob, setRecentJob] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [breakdowns, setBreakdowns] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyView, setHistoryView] = useState('jobs');
  const [historySearch, setHistorySearch] = useState('');
  const [historyPeriod, setHistoryPeriod] = useState('30d');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [appliedDateTo, setAppliedDateTo] = useState('');
  const [historyTotalBids, setHistoryTotalBids] = useState(0);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingVendorSheet, setDownloadingVendorSheet] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [myVendors, setMyVendors] = useState([]);
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [vendorReviewJobs, setVendorReviewJobs] = useState([]);
  const [vendorReviewTab, setVendorReviewTab] = useState('pending');
  const [vendorHistory, setVendorHistory] = useState([]);
  const [vendorReviewTotal, setVendorReviewTotal] = useState(0);
  const [vendorReviewPage, setVendorReviewPage] = useState(1);
  const [vendorReviewTotalPages, setVendorReviewTotalPages] = useState(1);
  const [vendorHistoryTotal, setVendorHistoryTotal] = useState(0);
  const [vendorHistoryPage, setVendorHistoryPage] = useState(1);
  const [vendorHistoryTotalPages, setVendorHistoryTotalPages] = useState(1);
  const [pendingLinkVendor, setPendingLinkVendor] = useState({});
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [flagVendorOpen, setFlagVendorOpen] = useState(false);
  const [flagVendorReason, setFlagVendorReason] = useState('');
  const [flagVendorSubmitting, setFlagVendorSubmitting] = useState(false);
  const [vendorReviewBusyId, setVendorReviewBusyId] = useState(null);
  const [bidNumber, setBidNumber] = useState(null);
  const [vendorNameCanonical, setVendorNameCanonical] = useState('');
  const [vendorCurrency, setVendorCurrency] = useState('USD');

  useEffect(() => {
    api.get('/bid-engine/my-vendors').then(r => setMyVendors(r.data.vendors || [])).catch(() => {});
  }, []);

  const fetchVendorReview = useCallback(() => {
    if (!isAdmin) return;
    api.get('/bid-engine/vendor-review', { params: { page: vendorReviewPage } }).then(r => {
      setVendorReviewJobs(r.data.jobs || []);
      setVendorReviewTotal(r.data.total || 0);
      setVendorReviewTotalPages(r.data.total_pages || 1);
      if (vendorReviewPage > (r.data.total_pages || 1)) setVendorReviewPage(r.data.total_pages || 1);
    }).catch(() => {});
    api.get('/bid-engine/vendor-review/history', { params: { page: vendorHistoryPage } }).then(r => {
      setVendorHistory(r.data.jobs || []);
      setVendorHistoryTotal(r.data.total || 0);
      setVendorHistoryTotalPages(r.data.total_pages || 1);
      if (vendorHistoryPage > (r.data.total_pages || 1)) setVendorHistoryPage(r.data.total_pages || 1);
    }).catch(() => {});
  }, [isAdmin, vendorReviewPage, vendorHistoryPage]);

  useEffect(() => { fetchVendorReview(); }, [fetchVendorReview]);

  const handleLinkVendor = async (jobId, zohoVendorId) => {
    if (!zohoVendorId) return;
    setVendorReviewBusyId(jobId);
    try {
      await api.post(`/bid-engine/vendor-review/${jobId}/link`, { zoho_vendor_id: zohoVendorId });
      toast.success('Linked to existing vendor');
      setVendorReviewJobs(j => j.filter(x => x.id !== jobId));
      setPendingLinkVendor(p => { const n = { ...p }; delete n[jobId]; return n; });
      setEditingHistoryId(null);
      fetchVendorReview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to link'); } finally { setVendorReviewBusyId(null); }
  };

  const handleConfirmNewVendor = async (jobId, createInZoho) => {
    setVendorReviewBusyId(jobId);
    try {
      const r = await api.post(`/bid-engine/vendor-review/${jobId}/confirm-new`, { create_in_zoho: createInZoho });
      toast.success(r.data.message); setVendorReviewJobs(j => j.filter(x => x.id !== jobId));
      fetchVendorReview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to confirm'); } finally { setVendorReviewBusyId(null); }
  };

  const vendorSuggestions = myVendors.filter(v => !vendorName.trim() || v.vendor_name.toLowerCase().includes(vendorName.trim().toLowerCase())).slice(0, 8);
  const isKnownVendor = myVendors.some(v => v.vendor_name.toLowerCase() === vendorName.trim().toLowerCase());

  useEffect(() => {
    api.get('/bid-engine/recent').then(r => { if (r.data && r.data.id) setRecentJob(r.data); }).catch(() => {});
  }, []);

  const loadHistory = (page = 1) => {
    setHistoryOpen(true); setHistoryLoading(true); setHistoryView('jobs'); setHistorySearch('');
    setHistoryPage(page); setHistoryPeriod('30d');
    const { from, to } = HISTORY_PERIOD_RANGE['30d']();
    setHistoryDateFrom(from); setHistoryDateTo(to); setAppliedDateFrom(from); setAppliedDateTo(to);
    api.get('/bid-engine/history', { params: { page, from, to } }).then(r => {
      setHistory(r.data.jobs || []); setBreakdowns(r.data.breakdowns || null); setHistoryTotalPages(r.data.total_pages || 1);
      setHistoryTotalBids(r.data.totals?.total_bids ?? 0);
    }).catch(() => setHistory([])).finally(() => setHistoryLoading(false));
  };

  // Round 36 (iteration_135 fix): pagination must always re-fetch the last APPLIED
  // range, never whatever is currently sitting in the (possibly mid-edit, unapplied)
  // Custom Range date inputs.
  const loadHistoryPage = (page, from = appliedDateFrom, to = appliedDateTo) => {
    if (page < 1 || page > historyTotalPages) return;
    setHistoryLoading(true); setHistoryPage(page);
    api.get('/bid-engine/history', { params: { page, from, to } }).then(r => {
      setHistory(r.data.jobs || []); setHistoryTotalPages(r.data.total_pages || 1);
      setHistoryTotalBids(r.data.totals?.total_bids ?? 0);
    }).catch(() => {}).finally(() => setHistoryLoading(false));
  };

  const applyHistoryPeriod = (period) => {
    setHistoryPeriod(period);
    if (period === 'custom') return;
    const { from, to } = HISTORY_PERIOD_RANGE[period]();
    setHistoryDateFrom(from); setHistoryDateTo(to); setAppliedDateFrom(from); setAppliedDateTo(to);
    setHistoryPage(1); setHistoryLoading(true);
    api.get('/bid-engine/history', { params: { page: 1, from, to } }).then(r => {
      setHistory(r.data.jobs || []); setBreakdowns(r.data.breakdowns || null); setHistoryTotalPages(r.data.total_pages || 1);
      setHistoryTotalBids(r.data.totals?.total_bids ?? 0);
    }).catch(() => setHistory([])).finally(() => setHistoryLoading(false));
  };

  const applyCustomRange = () => {
    if (!historyDateFrom || !historyDateTo) { toast.error('Pick both a from and to date'); return; }
    if (historyDateFrom > historyDateTo) { toast.error('From date must not be after the to date'); return; }
    setHistoryPage(1); setHistoryLoading(true);
    api.get('/bid-engine/history', { params: { page: 1, from: historyDateFrom, to: historyDateTo } }).then(r => {
      setHistory(r.data.jobs || []); setBreakdowns(r.data.breakdowns || null); setHistoryTotalPages(r.data.total_pages || 1);
      setHistoryTotalBids(r.data.totals?.total_bids ?? 0);
      setAppliedDateFrom(historyDateFrom); setAppliedDateTo(historyDateTo);
    }).catch((e) => toast.error(e.response?.data?.detail || 'Invalid date range')).finally(() => setHistoryLoading(false));
  };

  const filteredHistory = history.filter(h => {
    if (!historySearch.trim()) return true;
    const q = historySearch.toLowerCase();
    return (h.vendor_name || '').toLowerCase().includes(q) || (h.uploaded_by || '').toLowerCase().includes(q);
  });

  const BREAKDOWN_TABS = [
    { id: 'jobs', label: 'Jobs' },
    { id: 'by_buyer', label: 'By Buyer' },
    { id: 'by_team', label: 'By Team' },
    { id: 'by_vendor', label: 'By Vendor' },
    { id: 'by_country', label: 'By Country' },
    { id: 'by_category', label: 'By Category' },
    { id: 'by_pm', label: 'By PM' },
    { id: 'by_product', label: 'By Product' },
  ];

  const handleViewHistoryJob = async (jobId) => {
    try {
      const r = await api.get(`/bid-engine/status/${jobId}`);
      setResults(r.data.results || []); setStats(r.data.stats || {});
      setColumnsDetected(r.data.columns_detected);
      setCurrentJobId(jobId); setSubmitted(!!r.data.submitted); setSubmittedAt(r.data.submitted_at || null);
      setBidNumber(r.data.bid_number || null); setVendorNameCanonical(r.data.vendor_name_canonical || r.data.vendor_name || '');
      setHistoryOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load bid job'); }
  };

  useEffect(() => {
    const jobFromUrl = searchParams.get('job');
    if (jobFromUrl) handleViewHistoryJob(jobFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollJob = (jobId) => {
    setProcessing(true); setElapsed(0);
    let ticks = 0;
    const poll = setInterval(async () => {
      ticks += 1; setElapsed(ticks * 3);
      try {
        const status = await api.get(`/bid-engine/status/${jobId}`);
        if (status.data.status === 'completed') {
          clearInterval(poll);
          setResults(status.data.results || []); setStats(status.data.stats || {});
          setColumnsDetected(status.data.columns_detected);
          setCurrentJobId(jobId); setSubmitted(false); setSubmittedAt(null);
          setBidNumber(null); setVendorNameCanonical(status.data.vendor_name_canonical || status.data.vendor_name || '');
          setProcessing(false);
          toast.success(`Processed ${status.data.stats?.total || 0} items — ${status.data.stats?.high || 0} HIGH, ${status.data.stats?.buy || 0} BUY`);
          return;
        } else if (status.data.status === 'failed') {
          clearInterval(poll); setProcessing(false);
          toast.error(`Processing failed: ${status.data.error || 'Unknown error'}`);
          return;
        }
      } catch (e) { /* keep polling */ }
      if (ticks >= 400) { // ~20 min ceiling — job keeps running server-side even if we stop polling
        clearInterval(poll); setProcessing(false);
        toast.error('This is taking longer than expected. It may still be finishing in the background — reload to check again.');
      }
    }, 3000);
  };

  const handleProcess = async () => {
    if (!file) { toast.error('Please select a file'); return; }
    if (!vendorName.trim()) { toast.error('Vendor Name is required before processing a bid'); return; }
    try {
      const fd = new FormData(); fd.append('file', file);
      fd.append('vendor_name', vendorName.trim());
      fd.append('vendor_currency', vendorCurrency);
      const r = await api.post('/bid-engine/process', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 });
      setRecentJob(null);
      toast.info('Processing started — AI analyzing your file...');
      pollJob(r.data.job_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const handleResumeRecent = () => {
    if (!recentJob) return;
    if (recentJob.status === 'processing') {
      pollJob(recentJob.id);
    } else {
      setResults(recentJob.results || []); setStats(recentJob.stats || {});
      setColumnsDetected(recentJob.columns_detected);
      setCurrentJobId(recentJob.id); setSubmitted(!!recentJob.submitted); setSubmittedAt(recentJob.submitted_at || null);
      setBidNumber(recentJob.bid_number || null); setVendorNameCanonical(recentJob.vendor_name_canonical || recentJob.vendor_name || '');
      if (recentJob.status === 'failed') toast.error(`Processing failed: ${recentJob.error || 'Unknown error'}`);
    }
    setRecentJob(null);
  };

  const handleNewBid = () => {
    setFile(null); setVendorName(''); setResults(null); setStats(null); setColumnsDetected(null);
    setFilter('all'); setCurrentJobId(null); setSubmitted(false); setSubmittedAt(null); setRecentJob(null);
    setBidNumber(null); setVendorNameCanonical(''); setVendorCurrency('USD');
  };

  const handleSubmitBid = async () => {
    if (!currentJobId || !results) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/bid-engine/${currentJobId}/submit`, { results });
      setSubmitted(true); setSubmittedAt(r.data.submitted_at); setBidNumber(r.data.bid_number || null);
      setPreviewOpen(false); setConfirmOpen(true);
      toast.success('Bid submitted — this is now the official record in the system');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit bid'); } finally { setSubmitting(false); }
  };

  const handleFlagVendor = async () => {
    if (!currentJobId) return;
    setFlagVendorSubmitting(true);
    try {
      await api.post(`/bid-engine/${currentJobId}/flag-vendor`, { reason: flagVendorReason.trim() });
      toast.success('Flagged — an admin will review and re-link the correct vendor');
      setFlagVendorOpen(false); setFlagVendorReason('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to flag vendor'); }
    finally { setFlagVendorSubmitting(false); }
  };

  const handleDownloadVendorFormat = async () => {
    if (!currentJobId || !results) return;
    setDownloadingVendorSheet(true);
    try {
      const r = await api.post(`/bid-engine/${currentJobId}/vendor-format-excel`, { results });
      const bytes = atob(r.data.excel_base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = r.data.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate vendor bid sheet — original file may not be available for this bid'); }
    finally { setDownloadingVendorSheet(false); }
  };

  const handleDownload = () => {
    if (!results || !results.length) return;
    // Screen Capture & Export Protection (4 Nov 2026): built entirely in the browser
    // (includes Target Price and PM) — gate + log it via the one client-export check
    // before building the file.
    api.post('/audit/client-export', {
      module_id: 'BID_ENGINE', filename: `VSTLBID_${bidNumber ? String(bidNumber).padStart(5, '0') : '00000'}.xlsx`,
      row_count: results.length, job_id: currentJobId,
    }).then(() => {
      import('xlsx').then(XLSX => {
      const data = results.map(r => ({
        'Vendor Description': r.uploaded_item, 'Matched VSTL SKU': r.matched_sku || '',
        'Inventory Item': r.inventory_item || '', 'Match Confidence': r.confidence_level ? `${r.confidence_level} (${r.confidence}%)` : '',
        'Category': r.category || '', 'Qty': r.qty || '', 'Vendor Price': r.buyer_bid != null ? r.buyer_bid : '',
        'Target Price': r.target_price != null ? r.target_price : 'Not Available', 'Variance %': r.variance_pct != null ? `${r.variance_pct}%` : '',
        'Recommendation': r.recommendation || '', 'PM': r.assigned_pm || 'Not Assigned',
        'Match Type': r.match_type || '', 'Status': r.status, 'Notes': r.notes || '',
        'Detected Capacity': r.ai_attrs?.capacity || '', 'Detected DDR': r.ai_attrs?.generation || '',
        'Detected Speed': r.ai_attrs?.speed || '', 'Detected Type': r.ai_attrs?.ram_category || '',
        'Detected Module': r.ai_attrs?.module_type || '', 'Detected ECC': r.ai_attrs?.ecc != null ? String(r.ai_attrs.ecc) : '',
        'Detected Rank': r.ai_attrs?.rank || '', 'Detected Brand': r.ai_attrs?.brand || '',
        'Detected Voltage': r.ai_attrs?.voltage || '', 'Detected Organization': r.ai_attrs?.organization || '',
      }));
      const ws = XLSX.utils.json_to_sheet(data); const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bid Engine'); ws['!cols'] = Object.keys(data[0]).map(() => ({ wch: 22 }));
      const vendorLabel = (vendorNameCanonical || vendorName || 'Vendor').trim().replace(/\s+/g, '_');
      const numLabel = bidNumber ? String(bidNumber).padStart(5, '0') : '00000';
      XLSX.writeFile(wb, `VSTLBID_${numLabel}_${vendorLabel}.xlsx`);
      });
    }).catch(() => toast.error('Export is not allowed for your account.'));
  };

  const searchInventory = useCallback(async (q) => {
    setInvLoading(true);
    try { const r = await api.get('/zoho/items', { params: { search: q || undefined, limit: 30 } }); setInvResults(r.data.items || []); }
    catch { setInvResults([]); } finally { setInvLoading(false); }
  }, []);

  useEffect(() => { if (!changeRow) return; const t = setTimeout(() => searchInventory(invSearch), 300); return () => clearTimeout(t); }, [invSearch, changeRow, searchInventory]);

  const handleSelectItem = async (rowIndex, sku) => {
    setResolving(true);
    try {
      const uploadedText = results[rowIndex]?.uploaded_item || '';
      const buyerBid = results[rowIndex]?.buyer_bid;
      const r = await api.post('/bid-engine/resolve-match', { sku, uploaded_text: uploadedText, job_id: currentJobId, buyer_bid: buyerBid }); const d = r.data;
      setResults(prev => { const u = [...prev]; u[rowIndex] = { ...u[rowIndex], matched_sku: d.sku, inventory_item: d.name, category: d.category, assigned_pm: d.assigned_pm, target_price: d.target_price, match_type: 'MANUAL', confidence: 0, confidence_level: '', status: d.status, possible_matches: [], recommendation: d.recommendation || '', variance_pct: d.variance_pct, notes: 'Manually matched' }; return u; });
      setChangeRow(null); setInvSearch('');
      toast.success(d.learned ? `Matched to ${d.sku} — sent for admin review as a learned alias` : `Matched to ${d.sku}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setResolving(false); }
  };

  const filtered = results ? results.filter(r => {
    if (filter === 'all') return true;
    if (filter === 'buy') return r.recommendation === 'BUY';
    if (filter === 'negotiate') return r.recommendation === 'NEGOTIATE';
    if (filter === 'reject') return r.recommendation === 'REJECT';
    if (filter === 'high') return r.confidence_level === 'HIGH';
    if (filter === 'review') return r.status.includes('MANUAL MATCH') || r.status.includes('REVIEW') || r.confidence_level === 'LOW';
    if (filter === 'unmatched') return r.confidence_level === 'NO MATCH' || r.status.includes('NO RELIABLE');
    return true;
  }) : [];

  const getIdx = (item) => results ? results.indexOf(item) : -1;

  return (
    <div className="space-y-6" data-testid="bid-engine-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Zap className="h-6 w-6 text-primary" />Bid Engine</h1>
          <p className="text-muted-foreground text-sm">AI-powered matching for all VSTL products (CPUs, RAM, drives, servers, networking) with strict attribute validation</p>
          <ZohoSyncStamp className="mt-1" />
        </div>
        <Button variant="outline" className="rounded-sm" onClick={() => loadHistory(1)} data-testid="bid-history-btn"><History className="h-4 w-4 mr-2" />Bid History</Button>
      </div>

      {isAdmin && (vendorReviewTotal > 0 || vendorHistoryTotal > 0) && (
        <Card className="rounded-sm border-amber-200" data-testid="vendor-review-panel">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Button size="sm" variant={vendorReviewTab === 'pending' ? 'default' : 'outline'} className="h-7 text-xs rounded-sm" onClick={() => setVendorReviewTab('pending')} data-testid="vendor-review-tab-pending">
                Needing Review ({vendorReviewTotal})
              </Button>
              <Button size="sm" variant={vendorReviewTab === 'reviewed' ? 'default' : 'outline'} className="h-7 text-xs rounded-sm" onClick={() => setVendorReviewTab('reviewed')} data-testid="vendor-review-tab-reviewed">
                Already Reviewed ({vendorHistoryTotal})
              </Button>
            </div>

            {vendorReviewTab === 'pending' && (
              <>
                <p className="text-xs text-muted-foreground">These bids used a vendor name that didn't exactly match a known Zoho vendor (no match at all, or only a close/fuzzy one). Search and select the right vendor, then click Link — or confirm it's genuinely new.</p>
                <div className="space-y-2 max-h-[350px] overflow-y-auto">
                  {vendorReviewJobs.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-4 text-center">Nothing pending review.</p>
                  ) : vendorReviewJobs.map(j => (
                    <div key={j.id} className="p-3 border rounded-sm space-y-2" data-testid={`vendor-review-row-${j.id}`}>
                      <p className="text-sm font-medium">"{j.vendor_name}" <span className="text-xs text-muted-foreground font-normal">by {j.user_name || 'Unknown'} · {new Date(j.created_at).toLocaleDateString()}</span></p>
                      {j.vendor_flag_reason !== undefined && j.vendor_flagged_by && (
                        <p className="text-xs text-amber-700 bg-amber-50 rounded-sm px-2 py-1" data-testid={`vendor-flag-note-${j.id}`}>
                          Flagged by {j.vendor_flagged_by}{j.vendor_flag_reason ? `: "${j.vendor_flag_reason}"` : ' — vendor match is wrong'}
                        </p>
                      )}
                      {j.vendor_owner_mismatch && (
                        <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-2 py-1.5" data-testid={`vendor-owner-mismatch-${j.id}`}>
                          Exact name match found, but that vendor is owned by someone else — not linked automatically.
                        </p>
                      )}
                      {(j.vendor_suggestions || []).length > 0 && (
                        <div className="space-y-1" data-testid={`vendor-suggestions-${j.id}`}>
                          {j.vendor_suggestions.map((sug, idx) => (
                            <div key={idx} className="text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-sm px-2 py-1.5 flex items-center justify-between gap-2 flex-wrap">
                              <span>Did you mean <strong>{sug.vendor_name}</strong>? ({Math.round((sug.score || 0) * 100)}% match · owner: {sug.owner_name} — not applied automatically)</span>
                              <Button size="sm" variant="outline" className="h-6 text-xs rounded-sm bg-white" disabled={vendorReviewBusyId === j.id}
                                onClick={() => handleLinkVendor(j.id, sug.zoho_vendor_id)} data-testid={`confirm-suggestion-${j.id}-${idx}`}>
                                Confirm "{sug.vendor_name}"
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <VendorCombobox
                          vendors={myVendors}
                          value={pendingLinkVendor[j.id]}
                          onSelect={(id) => setPendingLinkVendor(p => ({ ...p, [j.id]: id }))}
                          placeholder="Search vendor..."
                          testId={`link-vendor-combobox-${j.id}`}
                        />
                        <Button size="sm" className="h-8 text-xs rounded-sm" disabled={!pendingLinkVendor[j.id] || vendorReviewBusyId === j.id} onClick={() => handleLinkVendor(j.id, pendingLinkVendor[j.id])} data-testid={`link-vendor-submit-${j.id}`}>
                          {vendorReviewBusyId === j.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}Link
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs rounded-sm" disabled={vendorReviewBusyId === j.id} onClick={() => handleConfirmNewVendor(j.id, false)} data-testid={`confirm-new-local-btn-${j.id}`}>Confirm New (Internal Only)</Button>
                        {isPreviewEnv ? (
                          <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button size="sm" className="h-8 text-xs rounded-sm" disabled data-testid={`confirm-new-zoho-btn-${j.id}`}>Confirm New + Create in Zoho</Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>Preview environment — writing to Zoho is disabled. Use production.</TooltipContent>
                          </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <Button size="sm" className="h-8 text-xs rounded-sm" disabled={vendorReviewBusyId === j.id} onClick={() => handleConfirmNewVendor(j.id, true)} data-testid={`confirm-new-zoho-btn-${j.id}`}>Confirm New + Create in Zoho</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {vendorReviewTotalPages > 1 && (
                  <div className="flex items-center justify-between pt-1" data-testid="vendor-review-pending-pagination">
                    <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" disabled={vendorReviewPage <= 1} onClick={() => setVendorReviewPage(p => p - 1)} data-testid="vendor-review-pending-prev">Previous</Button>
                    <span className="text-xs text-muted-foreground">Page {vendorReviewPage} of {vendorReviewTotalPages}</span>
                    <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" disabled={vendorReviewPage >= vendorReviewTotalPages} onClick={() => setVendorReviewPage(p => p + 1)} data-testid="vendor-review-pending-next">Next</Button>
                  </div>
                )}
              </>
            )}

            {vendorReviewTab === 'reviewed' && (
              <>
                <p className="text-xs text-muted-foreground">Vendor review decisions already made. Click Edit to re-link a bid to a different vendor.</p>
                <div className="space-y-2 max-h-[350px] overflow-y-auto">
                  {vendorHistory.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-4 text-center">No reviewed vendors yet.</p>
                  ) : vendorHistory.map(j => (
                    <div key={j.id} className="p-3 border rounded-sm space-y-2" data-testid={`vendor-history-row-${j.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-sm">
                          "{j.vendor_name}" <ArrowRight className="h-3 w-3 inline text-muted-foreground" /> <span className="font-medium">{j.vendor_name_canonical || j.vendor_name}</span>
                          <Badge variant="outline" className="ml-2 text-[10px] rounded-sm">{j.vendor_review_status === 'linked' ? 'Linked' : 'Confirmed New'}</Badge>
                        </div>
                        {editingHistoryId !== j.id && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs rounded-sm" onClick={() => { setEditingHistoryId(j.id); setPendingLinkVendor(p => ({ ...p, [j.id]: j.vendor_zoho_id })); }} data-testid={`edit-vendor-history-${j.id}`}>
                            <Pencil className="h-3 w-3 mr-1" />Edit
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">Reviewed by {j.vendor_reviewed_by || 'Unknown'} · {j.vendor_reviewed_at ? new Date(j.vendor_reviewed_at).toLocaleDateString() : ''}</p>
                      {editingHistoryId === j.id && (
                        <div className="flex items-center gap-2 flex-wrap pt-1">
                          <VendorCombobox
                            vendors={myVendors}
                            value={pendingLinkVendor[j.id]}
                            onSelect={(id) => setPendingLinkVendor(p => ({ ...p, [j.id]: id }))}
                            placeholder="Search vendor..."
                            testId={`edit-vendor-combobox-${j.id}`}
                          />
                          <Button size="sm" className="h-8 text-xs rounded-sm" disabled={!pendingLinkVendor[j.id] || vendorReviewBusyId === j.id} onClick={() => handleLinkVendor(j.id, pendingLinkVendor[j.id])} data-testid={`edit-vendor-submit-${j.id}`}>
                            {vendorReviewBusyId === j.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}Save
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-xs rounded-sm" onClick={() => setEditingHistoryId(null)} data-testid={`cancel-edit-vendor-${j.id}`}>Cancel</Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {vendorHistoryTotalPages > 1 && (
                  <div className="flex items-center justify-between pt-1" data-testid="vendor-review-history-pagination">
                    <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" disabled={vendorHistoryPage <= 1} onClick={() => setVendorHistoryPage(p => p - 1)} data-testid="vendor-review-history-prev">Previous</Button>
                    <span className="text-xs text-muted-foreground">Page {vendorHistoryPage} of {vendorHistoryTotalPages}</span>
                    <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" disabled={vendorHistoryPage >= vendorHistoryTotalPages} onClick={() => setVendorHistoryPage(p => p + 1)} data-testid="vendor-review-history-next">Next</Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm"><CardContent className="p-6">
        {recentJob && !results && !processing && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-sm flex items-center justify-between gap-3 text-sm" data-testid="recent-bid-job-banner">
            <div>
              <p className="font-medium text-blue-900">
                {recentJob.status === 'processing' ? 'A bid analysis is still running' : 'You have a recent bid analysis'} from {new Date(recentJob.created_at).toLocaleString()}
              </p>
              {recentJob.stats && <p className="text-xs text-blue-700">Total {recentJob.stats.total} · HIGH {recentJob.stats.high} · BUY {recentJob.stats.buy}</p>}
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => setRecentJob(null)} data-testid="dismiss-recent-bid-job-btn">Start New</Button>
              <Button size="sm" className="rounded-sm h-7 text-xs" onClick={handleResumeRecent} data-testid="resume-recent-bid-job-btn">
                {recentJob.status === 'processing' ? 'Resume' : 'View Results'}
              </Button>
            </div>
          </div>
        )}
        <div className="mb-4 max-w-sm relative">
          <label className="text-xs font-medium mb-1 block">Vendor Name <span className="text-destructive">*</span> <span className="text-muted-foreground font-normal">(for your record-keeping only)</span></label>
          <Input value={vendorName} onChange={e => { setVendorName(e.target.value); setVendorDropdownOpen(true); }} onFocus={() => setVendorDropdownOpen(true)} onBlur={() => setTimeout(() => setVendorDropdownOpen(false), 150)} placeholder="Type or select a vendor..." className="rounded-sm" data-testid="bid-vendor-name-input" autoComplete="off" required />
          {vendorDropdownOpen && vendorSuggestions.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-popover border rounded-sm shadow-md max-h-48 overflow-y-auto" data-testid="vendor-name-suggestions">
              {vendorSuggestions.map(v => (
                <div key={v.zoho_vendor_id || v.vendor_name} className="px-3 py-2 text-sm hover:bg-muted cursor-pointer" onMouseDown={() => { setVendorName(v.vendor_name); setVendorDropdownOpen(false); }} data-testid={`vendor-suggestion-${v.zoho_vendor_id || v.vendor_name}`}>
                  {v.vendor_name}{v.country && <span className="text-xs text-muted-foreground ml-1.5">({v.country})</span>}
                </div>
              ))}
            </div>
          )}
          {vendorName.trim() && !isKnownVendor && (
            <p className="text-[10px] text-amber-600 mt-1" data-testid="vendor-unrecognized-hint">New vendor name — your bid will still be accepted; an admin will review it.</p>
          )}
        </div>
        <div className="mb-4 max-w-[160px]">
          <label className="text-xs font-medium mb-1 block">Sheet Currency</label>
          <Select value={vendorCurrency} onValueChange={setVendorCurrency}>
            <SelectTrigger className="rounded-sm h-9" data-testid="bid-vendor-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {VENDOR_CURRENCIES.map(c => <SelectItem key={c} value={c} data-testid={`bid-vendor-currency-${c}`}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="border-2 border-dashed rounded-lg p-8 text-center">
          <input type="file" accept=".xlsx,.xls,.csv" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" id="bid-file" />
          <label htmlFor="bid-file" className="cursor-pointer"><Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" /><p className="text-lg font-medium">Upload Buying List</p><p className="text-sm text-muted-foreground">Any Excel/CSV format — AI detects products automatically</p></label>
          {file && <div className="mt-4 inline-flex items-center gap-2 p-2 bg-muted rounded-lg"><FileSpreadsheet className="h-5 w-5 text-primary" /><span className="font-medium">{file.name}</span></div>}
        </div>
        {columnsDetected && <div className="mt-3 text-xs text-muted-foreground">Detected: Product=<strong>{columnsDetected.product}</strong>{columnsDetected.qty && <>, Qty=<strong>{columnsDetected.qty}</strong></>}{columnsDetected.price && <>, Price=<strong>{columnsDetected.price}</strong></>}</div>}
        {processing && elapsed >= 15 && <p className="mt-2 text-xs text-muted-foreground text-center">{elapsed}s elapsed — large files with many new/unmatched SKUs can take several minutes.</p>}
        <div className="flex justify-end mt-4"><Button onClick={handleProcess} disabled={!file || !vendorName.trim() || processing} className="rounded-sm" data-testid="process-bid-btn">{processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}{processing ? 'AI Processing...' : 'Process Bid'}</Button></div>
      </CardContent></Card>

      {stats && <>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          <Card className="rounded-sm"><CardContent className="p-2.5"><p className="text-[9px] text-muted-foreground uppercase">Total</p><p className="text-lg font-bold font-mono">{stats.total}</p></CardContent></Card>
          <Card className="rounded-sm border-emerald-200"><CardContent className="p-2.5"><p className="text-[9px] text-emerald-600 uppercase">HIGH</p><p className="text-lg font-bold font-mono text-emerald-600">{stats.high}</p></CardContent></Card>
          <Card className="rounded-sm border-blue-200"><CardContent className="p-2.5"><p className="text-[9px] text-blue-600 uppercase">MEDIUM</p><p className="text-lg font-bold font-mono text-blue-600">{stats.medium}</p></CardContent></Card>
          <Card className="rounded-sm border-orange-200"><CardContent className="p-2.5"><p className="text-[9px] text-orange-600 uppercase">LOW</p><p className="text-lg font-bold font-mono text-orange-600">{stats.low}</p></CardContent></Card>
          <Card className="rounded-sm border-red-200"><CardContent className="p-2.5"><p className="text-[9px] text-red-600 uppercase">No Match</p><p className="text-lg font-bold font-mono text-red-600">{stats.no_match}</p></CardContent></Card>
          <Card className="rounded-sm bg-emerald-50"><CardContent className="p-2.5"><p className="text-[9px] text-emerald-700 uppercase">BUY</p><p className="text-lg font-bold font-mono text-emerald-700">{stats.buy}</p><p className="text-[9px] text-emerald-600">${stats.buy_value?.toLocaleString()}</p></CardContent></Card>
          <Card className="rounded-sm bg-amber-50"><CardContent className="p-2.5"><p className="text-[9px] text-amber-700 uppercase">NEGOTIATE</p><p className="text-lg font-bold font-mono text-amber-700">{stats.negotiate}</p></CardContent></Card>
          <Card className="rounded-sm bg-red-50"><CardContent className="p-2.5"><p className="text-[9px] text-red-700 uppercase">REJECT</p><p className="text-lg font-bold font-mono text-red-700">{stats.reject}</p></CardContent></Card>
        </div>
      </>}

      {results && <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1 flex-wrap">
          {[['all','All'],['buy','BUY'],['negotiate','NEGOTIATE'],['reject','REJECT'],['high','HIGH'],['review','Review'],['unmatched','No Match']].map(([k,l]) => (
            <Button key={k} variant={filter === k ? 'default' : 'outline'} size="sm" onClick={() => setFilter(k)} className="rounded-sm text-xs h-7">{l}</Button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {submitted ? (
            <Badge className="bg-emerald-100 text-emerald-800 rounded-sm" data-testid="bid-status-submitted">SUBMITTED {submittedAt && `· ${new Date(submittedAt).toLocaleString()}`}</Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-800 rounded-sm" data-testid="bid-status-draft">DRAFT — not yet submitted</Badge>
          )}
          {submitted && <>
            <Button onClick={handleDownload} variant="outline" size="sm" className="rounded-sm" data-testid="download-detailed-btn"><Download className="h-4 w-4 mr-2" />Detailed Excel</Button>
            <Button onClick={handleDownloadVendorFormat} disabled={downloadingVendorSheet} variant="outline" size="sm" className="rounded-sm" data-testid="download-vendor-format-btn">
              {downloadingVendorSheet ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}Vendor Bid Sheet
            </Button>
          </>}
          <Button onClick={handleNewBid} variant="outline" size="sm" className="rounded-sm" data-testid="new-bid-btn">New Bid</Button>
          {currentJobId && (
            <Button onClick={() => setFlagVendorOpen(true)} variant="outline" size="sm" className="rounded-sm text-amber-700 border-amber-300" data-testid="flag-wrong-vendor-btn">
              <AlertTriangle className="h-4 w-4 mr-2" />Wrong Vendor?
            </Button>
          )}
          {!submitted && (
            <Button onClick={() => setPreviewOpen(true)} size="sm" className="rounded-sm" data-testid="preview-vendor-sheet-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" />Preview Vendor Sheet
            </Button>
          )}
        </div>
      </div>}

      {filtered.length > 0 && <Card className="rounded-sm"><CardContent className="p-0"><HorizontalScrollTable>
        <Table><TableHeader><TableRow className="bg-muted/50">
          <TableHead className="text-xs font-medium min-w-[200px]">Vendor Description</TableHead>
          <TableHead className="text-xs font-medium">Matched SKU</TableHead>
          <TableHead className="text-xs font-medium min-w-[200px]">VSTL Inventory</TableHead>
          <TableHead className="text-xs font-medium text-center">Confidence</TableHead>
          <TableHead className="text-xs font-medium text-right">Qty</TableHead>
          <TableHead className="text-xs font-medium text-right">Vendor Price</TableHead>
          <TableHead className="text-xs font-medium text-right">Target Price</TableHead>
          <TableHead className="text-xs font-medium text-center">Variance</TableHead>
          <TableHead className="text-xs font-medium text-center">Recommend</TableHead>
          <TableHead className="text-xs font-medium">PM</TableHead>
          <TableHead className="text-xs font-medium">Match</TableHead>
          <TableHead className="text-xs font-medium min-w-[180px]">Status</TableHead>
        </TableRow></TableHeader>
        <TableBody>{filtered.map((r) => { const idx = getIdx(r); return (
          <TableRow key={idx} className={r.recommendation === 'BUY' ? 'bg-emerald-50/30' : r.recommendation === 'NEGOTIATE' ? 'bg-green-50/30' : r.recommendation === 'REJECT' ? 'bg-red-50/30' : ''}>
            <TableCell className="text-sm font-medium"><div className="whitespace-normal break-words">{r.uploaded_item}</div></TableCell>
            <TableCell className="font-mono text-xs">{r.matched_sku || '—'}</TableCell>
            <TableCell className="text-sm"><div className="whitespace-normal break-words">{r.inventory_item || '—'}</div></TableCell>
            <TableCell className="text-center"><span className={`text-xs font-mono font-medium ${r.confidence_level === 'HIGH' ? 'text-emerald-600' : r.confidence_level === 'MEDIUM' ? 'text-blue-600' : r.confidence_level === 'LOW' ? 'text-orange-600' : 'text-muted-foreground'}`}>{r.confidence_level || '—'}{r.confidence > 0 ? ` ${r.confidence}%` : ''}</span></TableCell>
            <TableCell className="text-right font-mono text-sm">{r.qty || '—'}</TableCell>
            <TableCell className="text-right font-mono text-sm">
              {r.buyer_bid != null ? (
                <TooltipProvider><Tooltip><TooltipTrigger asChild>
                  <div className={isAdmin && r.fx_market_rate != null ? 'cursor-help' : ''} data-testid={`bid-vendor-price-${idx}`}>
                    <div>${Number(r.buyer_bid).toFixed(2)}</div>
                    {r.vendor_currency_code && r.vendor_currency_code !== 'USD' && (
                      <div className="text-[10px] text-muted-foreground font-sans">{r.vendor_price_original} {r.vendor_currency_code}</div>
                    )}
                  </div>
                </TooltipTrigger>
                {isAdmin && r.fx_market_rate != null && (
                  <TooltipContent className="text-xs">
                    <div>Market rate: 1 {r.vendor_currency_code} = ${r.fx_market_rate} USD</div>
                    <div>Conversion charge: {r.fx_charge_pct_used}%</div>
                    <div>Applied rate: ${r.fx_applied_rate}</div>
                    <div>Source: {r.fx_source}</div>
                  </TooltipContent>
                )}
                </Tooltip></TooltipProvider>
              ) : '—'}
            </TableCell>
            <TableCell className="text-right font-mono text-sm">{r.target_price != null ? `$${r.target_price}` : '—'}</TableCell>
            <TableCell className="text-center font-mono text-xs">{r.variance_pct != null ? `${r.variance_pct}%` : '—'}</TableCell>
            <TableCell className="text-center">{r.recommendation ? <Badge className={`text-[10px] rounded-sm ${REC_COLORS[r.recommendation] || ''}`}>{r.recommendation}</Badge> : '—'}</TableCell>
            <TableCell className="text-sm">{r.assigned_pm || '—'}</TableCell>
            <TableCell>{r.match_type ? <Badge className={`text-[10px] rounded-sm ${MATCH_COLORS[r.match_type] || ''}`}>{r.match_type}</Badge> : '—'}</TableCell>
            <TableCell><div className="flex items-center gap-1">
              <Badge variant="outline" className={`text-[10px] rounded-sm whitespace-normal ${STATUS_COLORS[r.status] || (r.status.startsWith('NEEDS_REVIEW') ? 'bg-orange-100 text-orange-800' : '')}`}>{r.status.replace(' - DO MANUAL MATCH','')}</Badge>
              {!submitted && <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-primary shrink-0" onClick={() => { setChangeRow({ ...r, _idx: idx }); setInvSearch(''); setInvResults([]); }} data-testid={`change-match-btn-${idx}`}>Change</Button>}
            </div>
            {r.ai_attrs && !r.ai_attrs._ai_failed && (
              <div className="mt-1 text-[9px] text-muted-foreground leading-tight">
                {[r.ai_attrs.capacity, r.ai_attrs.generation, r.ai_attrs.speed, r.ai_attrs.module_type, r.ai_attrs.ecc === true ? 'ECC' : null, r.ai_attrs.rank, r.ai_attrs.brand, r.ai_attrs.voltage].filter(Boolean).join(' | ')}
              </div>
            )}</TableCell>
          </TableRow>); })}</TableBody></Table>
      </HorizontalScrollTable></CardContent></Card>}

      {results && <p className="text-xs text-muted-foreground text-center">Showing {filtered.length} of {results.length} items | AI processed: {stats?.ai_processed || 0} items</p>}

      <Dialog open={!!changeRow} onOpenChange={() => { setChangeRow(null); setInvSearch(''); }}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>Change Inventory Item</DialogTitle></DialogHeader>
          {changeRow && <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
            <div className="p-3 bg-muted rounded-sm shrink-0">
              <p className="text-xs text-muted-foreground">Vendor Product</p>
              <p className="text-sm font-medium">{changeRow.uploaded_item}</p>
              {changeRow.matched_sku && <p className="text-xs text-muted-foreground mt-1">Current: <span className="font-mono">{changeRow.matched_sku}</span> — {changeRow.inventory_item}</p>}
              {changeRow.notes && <p className="text-xs text-amber-600 mt-1">{changeRow.notes}</p>}
            </div>
            {changeRow.possible_matches?.length > 0 && <div className="shrink-0 max-h-[150px] overflow-y-auto">
              <p className="text-xs font-semibold text-muted-foreground mb-1">Suggested Matches</p>
              {changeRow.possible_matches.map((m, j) => (
                <div key={j} className="flex items-center justify-between p-2 border rounded-sm mb-1 hover:bg-muted/30 cursor-pointer" onClick={() => !resolving && handleSelectItem(changeRow._idx, m.sku)}>
                  <div><span className="font-mono text-xs text-primary">{m.sku}</span><span className="text-sm ml-2">{m.name}</span></div>
                  <Button variant="outline" size="sm" className="h-6 text-xs shrink-0" disabled={resolving}>Select</Button>
                </div>))}
            </div>}
            <div className="shrink-0"><p className="text-xs font-semibold text-muted-foreground mb-1">Search Full Inventory</p>
              <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input value={invSearch} onChange={e => setInvSearch(e.target.value)} placeholder="Search SKU, name, model..." className="pl-9 rounded-sm" autoComplete="off" /></div></div>
            <div className="flex-1 min-h-0 border rounded-sm overflow-y-auto" style={{maxHeight: '250px'}}>
              {invLoading ? <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
              : invResults.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">{invSearch ? 'No items found' : 'Type to search'}</div>
              : invResults.map((item, j) => (
                <div key={j} className="flex items-center justify-between px-3 py-2.5 border-b last:border-b-0 hover:bg-muted/30 cursor-pointer" onClick={() => !resolving && handleSelectItem(changeRow._idx, item.sku)}>
                  <div className="flex-1 min-w-0"><div className="flex items-center gap-2"><span className="font-mono text-xs font-medium text-primary">{item.sku}</span>{item.assigned_to_name && <Badge variant="outline" className="text-[9px]">{item.assigned_to_name}</Badge>}</div><p className="text-sm truncate">{item.name}</p></div>
                  <Button variant="ghost" size="sm" className="h-7 shrink-0 ml-2" disabled={resolving}>{resolving ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRight className="h-3 w-3" />}</Button>
                </div>))}
            </div>
          </div>}
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="vendor-sheet-preview-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5" />Vendor Sheet Preview</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">This is exactly what will be sent back to the vendor — their original file with VSTL's bid price appended. Review, then Submit Bid to finalize.</p>
          <div className="flex-1 overflow-y-auto border rounded-sm">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead className="text-xs font-medium">Vendor Description</TableHead>
                <TableHead className="text-xs font-medium text-right">Qty</TableHead>
                <TableHead className="text-xs font-medium text-right">VSTL Bid Price ({vendorCurrency !== 'USD' ? vendorCurrency : 'USD'})</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(results || []).map((r, i) => (
                  <TableRow key={i} data-testid={`vendor-preview-row-${i}`}>
                    <TableCell className="text-sm"><div className="whitespace-normal break-words">{r.uploaded_item}</div></TableCell>
                    <TableCell className="text-right font-mono text-sm">{r.qty || '—'}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{r.target_price != null ? (vendorCurrency !== 'USD' && r.fx_applied_rate ? `${(r.target_price * r.fx_applied_rate).toFixed(2)} ${vendorCurrency}` : `$${r.target_price}`) : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="rounded-sm" data-testid="preview-cancel-btn">Cancel</Button>
            <Button onClick={handleSubmitBid} disabled={submitting} className="rounded-sm" data-testid="preview-submit-bid-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Submit Bid
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="bid-submit-confirm-modal">
          <DialogHeader><DialogTitle>Bid Submitted Successfully</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This bid is now locked as the official submitted record. Download the files below to send to the vendor or your team.</p>
          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={handleDownload} variant="outline" className="rounded-sm justify-start" data-testid="confirm-download-detailed-btn"><Download className="h-4 w-4 mr-2" />Download Detailed Excel</Button>
            <Button onClick={handleDownloadVendorFormat} disabled={downloadingVendorSheet} variant="outline" className="rounded-sm justify-start" data-testid="confirm-download-vendor-btn">
              {downloadingVendorSheet ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}Download Vendor Bid Sheet
            </Button>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={() => setConfirmOpen(false)} className="rounded-sm" data-testid="confirm-close-btn">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="bid-history-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" />Bid History</DialogTitle></DialogHeader>

          <div className="flex flex-wrap items-center gap-2 border-b pb-3" data-testid="bid-history-period-controls">
            <Select value={historyPeriod} onValueChange={applyHistoryPeriod}>
              <SelectTrigger className="h-8 w-44 rounded-sm text-xs" data-testid="bid-history-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HISTORY_PERIODS.map(p => <SelectItem key={p.id} value={p.id} data-testid={`bid-history-period-${p.id}`}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {historyPeriod === 'custom' && (
              <>
                <Input type="date" value={historyDateFrom} onChange={e => setHistoryDateFrom(e.target.value)}
                  className="h-8 w-36 rounded-sm text-xs" data-testid="bid-history-custom-from" />
                <span className="text-xs text-muted-foreground">to</span>
                <Input type="date" value={historyDateTo} onChange={e => setHistoryDateTo(e.target.value)}
                  className="h-8 w-36 rounded-sm text-xs" data-testid="bid-history-custom-to" />
                <Button size="sm" className="h-8 rounded-sm text-xs" onClick={applyCustomRange} data-testid="bid-history-custom-apply">Apply</Button>
              </>
            )}
            <span className="text-xs text-muted-foreground ml-auto" data-testid="bid-history-summary">
              {historyLoading ? '…' : (
                `${historyTotalBids} bid${historyTotalBids === 1 ? '' : 's'}` +
                (appliedDateFrom && appliedDateTo ? `, ${appliedDateFrom} to ${appliedDateTo}` : ', all time')
              )}
            </span>
          </div>

          {isAdmin && breakdowns && (
            <div className="flex gap-1.5 flex-wrap border-b pb-3">
              {BREAKDOWN_TABS.map(t => (
                <button key={t.id} onClick={() => setHistoryView(t.id)}
                  className={`text-xs px-2.5 py-1.5 rounded-sm font-medium transition-colors ${historyView === t.id ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70 text-muted-foreground'}`}
                  data-testid={`bid-history-tab-${t.id}`}>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {historyView === 'jobs' && (
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={historySearch} onChange={e => setHistorySearch(e.target.value)} placeholder="Search by vendor or uploader name..." className="pl-8 rounded-sm h-9" data-testid="bid-history-search" />
            </div>
          )}

          <div className="flex-1 overflow-y-auto space-y-2">
            {historyLoading ? (
              <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
            ) : historyView === 'jobs' ? (
              filteredHistory.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">No bid history found</div>
              ) : filteredHistory.map(h => (
                <div key={h.id} className="flex items-center justify-between p-3 border rounded-sm hover:bg-muted/30" data-testid={`bid-history-row-${h.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{new Date(h.created_at).toLocaleString()} <span className="text-muted-foreground">by {h.uploaded_by || 'Unknown'}</span>
                      {h.vendor_owner_view && <Badge variant="outline" className="ml-2 rounded-sm bg-violet-50 text-violet-700 text-[10px]" data-testid={`vendor-owner-badge-${h.id}`}>Your Vendor</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {h.vendor_name && <>Vendor: <span className="font-medium">{h.vendor_name}</span>
                        {h.vendor_name_canonical && <span className="italic"> (matched: {h.vendor_name_canonical})</span>}
                        {h.vendor_country && <> · {h.vendor_country}</>} · </>}
                      Total {h.stats?.total ?? 0} · BUY {h.stats?.buy ?? 0} · NEGOTIATE {h.stats?.negotiate ?? 0} · REJECT {h.stats?.reject ?? 0}
                      {h.matched_items_count != null && <> · {h.matched_items_count} item(s) in your product</>}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs shrink-0" onClick={() => handleViewHistoryJob(h.id)} data-testid={`bid-history-view-${h.id}`}>View</Button>
                </div>
              ))
            ) : (
              <BreakdownList view={historyView} data={breakdowns?.[historyView] || []} />
            )}
          </div>
          {historyView === 'jobs' && !historySearch.trim() && historyTotalPages > 1 && (
            <div className="flex items-center justify-between pt-2 border-t" data-testid="bid-history-pagination">
              <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" disabled={historyPage <= 1 || historyLoading}
                onClick={() => loadHistoryPage(historyPage - 1)} data-testid="bid-history-prev-page">Previous</Button>
              <span className="text-xs text-muted-foreground">Page {historyPage} of {historyTotalPages}</span>
              <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" disabled={historyPage >= historyTotalPages || historyLoading}
                onClick={() => loadHistoryPage(historyPage + 1)} data-testid="bid-history-next-page">Next</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={flagVendorOpen} onOpenChange={setFlagVendorOpen}>
        <DialogContent className="sm:max-w-md" data-testid="flag-vendor-modal">
          <DialogHeader>
            <DialogTitle>Flag Wrong Vendor</DialogTitle>
            <DialogDescription>
              This bid will be sent to an admin to re-link it to the correct vendor. You'll keep seeing it in your own Bid History once resolved.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={flagVendorReason} onChange={e => setFlagVendorReason(e.target.value)}
            placeholder="Optional: what's wrong with the vendor match? (e.g. correct vendor is XYZ Trading, not this one)"
            className="rounded-sm" rows={3} data-testid="flag-vendor-reason-input" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setFlagVendorOpen(false)} data-testid="flag-vendor-cancel-btn">Cancel</Button>
            <Button size="sm" className="rounded-sm" disabled={flagVendorSubmitting} onClick={handleFlagVendor} data-testid="flag-vendor-submit-btn">
              {flagVendorSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}Submit for Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BreakdownList({ view, data }) {
  if (!data.length) return <div className="py-8 text-center text-sm text-muted-foreground">No data yet</div>;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="space-y-1.5">
      {data.map((d, i) => {
        const label = view === 'by_product' ? `${d.sku}${d.name ? ' — ' + d.name : ''}` : d.name;
        return (
          <div key={i} className="flex items-center gap-3 text-sm" data-testid={`bid-breakdown-row-${i}`}>
            <span className="w-8 text-right text-xs text-muted-foreground shrink-0">{d.count}</span>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between mb-0.5"><span className="truncate">{label}</span></div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${Math.max(4, (d.count / max) * 100)}%` }} /></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
